import React from 'react';
import { motion } from 'motion/react';
import { Play, Camera, ChevronDown, MapPin, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { AppConfig } from '../types';
import { EditableText } from './EditableText';

interface HeroProps {
  studioName: string;
  tagline: string;
  description: string;
  city: string;
  weddingsCount?: number;
  chateauxCount?: number;
  isVisualEditActive?: boolean;
  onUpdateConfig?: (partialConfig: Partial<AppConfig>) => void;
  contact?: AppConfig['contact'];
}

function AnimatedCounter({
  target,
  suffix = '',
  duration = 2200,
  delay = 800
}: {
  target: number;
  suffix?: string;
  duration?: number;
  delay?: number;
}) {
  const [count, setCount] = React.useState(0);
  const ref = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    if (!target || target <= 0) {
      setCount(0);
      return;
    }

    let animationFrameId: number;
    let startTime: number | null = null;
    let delayTimer: ReturnType<typeof setTimeout> | null = null;
    let hasRun = false;

    const startAnimation = () => {
      if (hasRun) return;
      hasRun = true;
      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Cubic ease-out curve for smooth acceleration then fluid deceleration
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        setCount(Math.floor(easeOutCubic * target));

        if (progress < 1) {
          animationFrameId = requestAnimationFrame(animate);
        } else {
          setCount(target);
        }
      };
      animationFrameId = requestAnimationFrame(animate);
    };

    const triggerWithDelay = () => {
      if (delayTimer || hasRun) return;
      delayTimer = setTimeout(() => {
        startAnimation();
      }, delay);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          triggerWithDelay();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (delayTimer) clearTimeout(delayTimer);
      observer.disconnect();
    };
  }, [target, duration, delay]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
}

export function Hero({
  studioName,
  tagline,
  description,
  city,
  weddingsCount = 120,
  chateauxCount = 35,
  isVisualEditActive = false,
  onUpdateConfig,
  contact
}: HeroProps) {
  const { lang, t } = useLanguage();

  const scrollToGalleries = () => {
    document.getElementById('galleries')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Smart localization detection for custom admin text vs default text
  const displayTagline = tagline === "L'art du mouvement & la poésie de la lumière" ? t('hero_tagline') : tagline;
  const displayDescription = description.startsWith("LIFE-CAPTURED est") || description.startsWith("LIFE-CAPTURED is")
    ? t('hero_description')
    : description;

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-theme-bg pt-20">
      {/* Background Image / Video Ambient Layer */}
      <div className="absolute inset-0 z-0 opacity-55">
        <div className="absolute inset-0 bg-gradient-to-b from-theme-bg/70 via-transparent to-theme-bg z-10" />
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          className="w-full h-full object-cover scale-105 filter saturate-50 brightness-75 transition-all duration-1000"
          src="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4"
        />
      </div>

      {/* Decorative Theme-Accented Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-theme-accent/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full bg-theme-accent/10 blur-[150px] pointer-events-none" />

      {/* Content Container */}
      <div className="relative z-20 max-w-5xl mx-auto px-6 text-center pt-12 md:pt-20">
        {/* Localization Microtag */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-theme-card border border-theme-border backdrop-blur-md mb-8"
        >
          <MapPin className="w-3.5 h-3.5 text-theme-accent" />
          <EditableText
            value={city}
            onSave={(val) => onUpdateConfig?.({ contact: { ...contact, city: val } as any })}
            isEditingMode={isVisualEditActive}
            label="Ville principale"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-theme-accent font-mono">
              {city} & {lang === 'fr' ? "Bassin d'Arcachon" : "Arcachon Bay"}
            </span>
          </EditableText>
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
        </motion.div>

        {/* Brand Display Name Container */}
        <div className="relative inline-block mb-6">
          {/* Suspended aesthetic lantern with realistic organic candle flicker */}
          <div className="absolute -left-12 md:-left-24 -top-24 md:-top-36 w-24 h-48 md:w-36 md:h-64 pointer-events-none z-10 select-none flex flex-col items-center">
            {/* Hanging chain/wire */}
            <div className="w-[1.5px] h-20 md:h-28 bg-gradient-to-b from-white/0 via-theme-accent/60 to-theme-accent" />
            
            {/* Lantern frame */}
            <div className="relative w-12 h-16 md:w-16 md:h-24">
              {/* Flicker light aura */}
              <motion.div
                animate={{
                  opacity: [0.35, 0.8, 0.55, 0.9, 0.45, 0.7, 0.4, 0.85],
                  scale: [0.96, 1.05, 0.98, 1.1, 0.95, 1.03, 0.97, 1.08],
                }}
                transition={{
                  duration: 4.2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-[-12px] md:inset-[-20px] bg-theme-accent/15 rounded-full pointer-events-none mix-blend-screen z-0 blur-2xl"
              />

              {/* Lantern SVG frame */}
              <svg viewBox="0 0 100 150" className="w-full h-full text-theme-accent drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] relative z-10">
                {/* Top loop */}
                <circle cx="50" cy="15" r="8" fill="none" stroke="currentColor" strokeWidth="2.5" />
                {/* Top cap */}
                <path d="M 35,30 L 50,22 L 65,30 Z" fill="currentColor" />
                <path d="M 25,45 L 35,30 L 65,30 L 75,45 Z" fill="currentColor" stroke="currentColor" strokeWidth="1" />
                
                {/* Glass body outlines */}
                <path d="M 28,45 L 35,110 L 65,110 L 72,45 Z" fill="none" stroke="currentColor" strokeWidth="2.5" />
                
                {/* Bottom cap */}
                <rect x="30" y="110" width="40" height="10" fill="currentColor" rx="1" />
                <path d="M 34,120 L 50,135 L 66,120 Z" fill="currentColor" />
                
                {/* Protective metal wire cage arches */}
                <path d="M 28,45 C 40,60 40,95 35,110" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.7" />
                <path d="M 72,45 C 60,60 60,95 65,110" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.7" />
                <line x1="50" y1="45" x2="50" y2="110" stroke="currentColor" strokeWidth="1" opacity="0.6" />
                
                {/* Horizontal wire bars */}
                <path d="M 30,65 Q 50,70 70,65" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.8" />
                <path d="M 32,90 Q 50,95 68,90" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.8" />

                {/* Internal Candle block inside the glass */}
                <g className="translate-y-5">
                  <rect x="42" y="75" width="16" height="20" fill="currentColor" fillOpacity="0.3" stroke="currentColor" strokeWidth="1" />
                  <ellipse cx="50" cy="75" rx="8" ry="2" fill="currentColor" fillOpacity="0.5" />
                  <line x1="50" y1="75" x2="50" y2="69" stroke="#222" strokeWidth="1.5" />
                </g>
              </svg>

              {/* Dynamic Candle Flame Layer */}
              <div className="absolute top-[58%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-4 h-6 md:w-5 md:h-8 z-20 flex items-center justify-center pointer-events-none">
                <motion.div
                  animate={{
                    scaleY: [1, 1.2, 0.92, 1.25, 1.01, 1.15, 0.94, 1.1],
                    scaleX: [1, 0.9, 1.1, 0.85, 1.04, 0.92, 1.06, 0.97],
                    y: [0, -1, 1, -2, 0, -1, 0, -1.5],
                    opacity: [0.8, 1, 0.7, 1, 0.9, 1, 0.75, 0.95]
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="w-2.5 h-4 md:w-3.5 md:h-5 bg-gradient-to-t from-orange-500 via-amber-400 to-yellow-100 rounded-b-full rounded-t-full shadow-[0_0_10px_#f59e0b] origin-bottom"
                />
              </div>
            </div>
          </div>

          {/* Sublime rotating engagement ring overlay */}
          <motion.div
            className="absolute -right-16 md:-right-24 -top-16 md:-top-24 w-40 h-40 md:w-64 md:h-64 pointer-events-none z-[-1] select-none"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, ease: "linear", duration: 30 }}
          >
            <svg viewBox="0 0 200 200" className="w-full h-full text-theme-accent/40 drop-shadow-[0_0_15px_rgba(251,191,36,0.25)]">
              {/* Gold Ring Band */}
              <circle cx="100" cy="115" r="60" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <circle cx="100" cy="115" r="54" fill="none" stroke="currentColor" strokeWidth="0.5" strokeOpacity="0.4" />
              
              {/* Crown / Setting */}
              <path d="M 85,58 L 90,51 L 110,51 L 115,58 Z" fill="none" stroke="currentColor" strokeWidth="1" />
              
              {/* Solitaire Diamond Facets */}
              <polygon points="92,38 108,38 113,44 87,44" fill="none" stroke="currentColor" strokeWidth="0.75" />
              <polygon points="87,44 113,44 100,51" fill="none" stroke="currentColor" strokeWidth="0.75" />
              <polygon points="92,38 87,44 95,44" fill="none" stroke="currentColor" strokeWidth="0.5" />
              <polygon points="108,38 113,44 105,44" fill="none" stroke="currentColor" strokeWidth="0.5" />
              <line x1="100" y1="38" x2="100" y2="44" stroke="currentColor" strokeWidth="0.5" />
              <line x1="95" y1="44" x2="100" y2="51" stroke="currentColor" strokeWidth="0.5" />
              <line x1="105" y1="44" x2="100" y2="51" stroke="currentColor" strokeWidth="0.5" />

              {/* Glimmer Rays */}
              <line x1="100" y1="28" x2="100" y2="33" stroke="currentColor" strokeWidth="0.75" />
              <line x1="77" y1="35" x2="82" y2="38" stroke="currentColor" strokeWidth="0.75" />
              <line x1="123" y1="35" x2="118" y2="38" stroke="currentColor" strokeWidth="0.75" />
            </svg>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-serif tracking-wider text-theme-text font-light select-none pr-4 whitespace-nowrap"
          >
            <EditableText
              value={studioName}
              onSave={(val) => onUpdateConfig?.({ studioName: val })}
              isEditingMode={isVisualEditActive}
              label="Nom du Studio"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-b from-theme-text via-theme-text/90 to-theme-secondary/40">
                {studioName}
              </span>
            </EditableText>
          </motion.h1>
        </div>

        {/* Elegant spacer */}
        <div className="h-8 md:h-12" />

        {/* High-End Subtitle / Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="text-lg md:text-2xl font-serif text-theme-text/90 italic tracking-wide max-w-3xl mx-auto mb-8 font-light"
        >
          <EditableText
            value={tagline}
            onSave={(val) => onUpdateConfig?.({ tagline: val })}
            isEditingMode={isVisualEditActive}
            label="Slogan principal (Tagline)"
          >
            <span>{displayTagline}</span>
          </EditableText>
        </motion.p>

        {/* Short Editorial Description */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1 }}
          className="text-sm md:text-base text-theme-secondary tracking-wide max-w-2xl mx-auto leading-relaxed mb-12"
        >
          <EditableText
            value={description}
            onSave={(val) => onUpdateConfig?.({ description: val })}
            isEditingMode={isVisualEditActive}
            label="Description d'accueil"
            multiline
          >
            <span>{displayDescription}</span>
          </EditableText>
        </motion.p>

        {/* Interactive Luxury CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-5"
        >
          {/* Main Booking Action */}
          <button
            onClick={scrollToContact}
            aria-label="Réserver notre reportage de mariage"
            title="Réserver notre reportage"
            className="w-full sm:w-auto px-8 py-4 bg-theme-accent text-theme-bg font-semibold text-xs uppercase tracking-[0.2em] transition-all duration-300 shadow-md hover:opacity-90 transform hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
            id="hero-book-btn"
          >
            {lang === 'fr' ? 'Réserver notre reportage' : 'Book our services'}
          </button>

          {/* Secondary Discovery Action */}
          <button
            onClick={scrollToGalleries}
            aria-label="Explorer le portfolio de mariage"
            title="Explorer le portfolio"
            className="w-full sm:w-auto px-8 py-4 bg-transparent hover:bg-theme-card-hover border border-theme-border hover:border-theme-border-hover text-theme-text font-semibold text-xs uppercase tracking-[0.2em] transition-all duration-300 flex items-center justify-center gap-2 transform hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
            id="hero-explore-btn"
          >
            <Camera className="w-4 h-4 text-theme-accent" />
            {lang === 'fr' ? 'Explorer le portfolio' : 'Explore portfolio'}
          </button>
        </motion.div>

        {/* Dynamic Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto pt-8 border-t border-theme-border"
        >
          <div className="text-center">
            <p className="text-2xl font-mono text-theme-text font-bold">
              <EditableText
                value={weddingsCount}
                onSave={(val) => onUpdateConfig?.({ weddingsCount: parseInt(val, 10) || 0 })}
                isEditingMode={isVisualEditActive}
                label="Nombre de mariages"
                type="number"
              >
                <span>
                  <AnimatedCounter target={weddingsCount} suffix="+" duration={2200} delay={600} />
                </span>
              </EditableText>
            </p>
            <p className="text-[10px] uppercase tracking-wider text-theme-secondary">
              {lang === 'fr' ? 'Mariages Sublimés' : 'Weddings Captured'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-mono text-theme-text font-bold">
              <EditableText
                value={chateauxCount}
                onSave={(val) => onUpdateConfig?.({ chateauxCount: parseInt(val, 10) || 0 })}
                isEditingMode={isVisualEditActive}
                label="Châteaux & Domaines"
                type="number"
              >
                <span>
                  <AnimatedCounter target={chateauxCount} suffix="+" duration={2200} delay={600} />
                </span>
              </EditableText>
            </p>
            <p className="text-[10px] uppercase tracking-wider text-theme-secondary">
              {lang === 'fr' ? 'Châteaux & Domaines' : 'Castles & Estates'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-mono text-theme-text font-bold">4K HDR</p>
            <p className="text-[10px] uppercase tracking-wider text-theme-secondary">
              {lang === 'fr' ? 'Qualité Cinéma' : 'Cinema Quality'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-mono text-theme-accent font-bold flex items-center justify-center gap-1">
              <AnimatedCounter target={100} suffix="%" duration={2200} delay={600} />
              <Sparkles className="w-3.5 h-3.5 text-theme-accent inline" />
            </p>
            <p className="text-[10px] uppercase tracking-wider text-theme-secondary">
              {lang === 'fr' ? 'Satisfaction Prestige' : 'Prestige Satisfaction'}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Downward Scroll Indicator */}
      <button
        onClick={scrollToGalleries}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 text-theme-secondary hover:text-theme-text transition-colors duration-300 animate-bounce cursor-pointer"
        id="scroll-indicator"
        aria-label="Défiler vers le portfolio de mariage"
        title="Défiler vers le bas"
      >
        <ChevronDown className="w-8 h-8" />
      </button>
    </section>
  );
}

